import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { formatDateDMY } from '../utils/dateUtils';

// The company's Terms & Conditions in a scrollable document, the same way —
// and with the same look — as the loan agreement review (ContractPreview):
// a section card, an amber "read to the end" banner, the navy page chrome
// and company header, and a fixed-height scroll area. onReadToEnd fires once
// the reader reaches the bottom; the caller keeps "I agree" locked until then.
//
// The text comes from the website through GET /api/terms (already cleaned to
// plain headings, paragraphs and lists on the server).
const TermsAndConditionsViewer = ({ onReadToEnd, readToEnd = false, className = '' }) => {
  const [terms, setTerms] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [error, setError] = useState('');
  const [justFinished, setJustFinished] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(apiUrl('/api/terms'));
        if (!cancelled) setTerms(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || 'The Terms & Conditions could not be loaded.');
          setTerms({ page_url: err.response?.data?.page_url || 'https://konizafs.com/terms-and-conditions/' });
        }
      }
      try {
        const res = await axios.get(apiUrl('/api/company-config/public'));
        if (!cancelled && res.data?.company_name) setCompanyName(res.data.company_name);
      } catch { /* header falls back to a generic name */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // A short document that needs no scrolling counts as read once it is shown.
  // If the terms cannot be loaded here the client is sent to the website for
  // them, and is not held up from agreeing.
  useEffect(() => {
    if (readToEnd) return;
    if (error) { onReadToEnd?.(); return; }
    const el = scrollRef.current;
    if (!terms?.html || !el) return;
    if (el.scrollHeight <= el.clientHeight + 20) onReadToEnd?.();
  }, [terms, error, readToEnd, onReadToEnd]);

  // Same rule as the contract review: within 20px of the bottom.
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || readToEnd) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 20) {
      onReadToEnd?.();
      setJustFinished(true);
      setTimeout(() => setJustFinished(false), 3000);
    }
  };

  const updated = terms?.modified
    ? formatDateDMY(terms.modified)
    : null;

  return (
    <div className={`border-2 border-gray-900 dark:border-slate-600 overflow-hidden rounded-none bg-white dark:bg-slate-800 ${className}`}>
      <div className="bg-slate-600 text-white px-3 py-2 font-semibold text-xs uppercase tracking-wide">
        Terms &amp; Conditions
      </div>

      {/* Read-instruction banner sits outside the scrollable document */}
      <div className={`border-b px-4 py-2 text-center text-xs ${
        readToEnd
          ? 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-900/20 dark:border-emerald-800/40 dark:text-emerald-300'
          : 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800/40 dark:text-amber-300'
      }`}>
        {readToEnd
          ? (justFinished ? 'Thank you for reading the terms. You can now continue.' : 'You have read the Terms & Conditions.')
          : 'Please scroll through and read the entire Terms & Conditions before you agree'}
      </div>

      <div className="p-3 sm:p-4 bg-gray-100 dark:bg-slate-700">
        <div className="mx-auto max-w-[1020px] bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 shadow-md">
          {/* Page chrome: accent bars matching the contract */}
          <div className="h-[10px] bg-[#1a365d]" />
          <div className="h-[2px] bg-[#2563eb]" />
          <div className="bg-[#1a365d] px-4 py-3 text-center" style={{ borderBottom: '3px solid #2563eb', fontFamily: 'Helvetica, Arial, sans-serif' }}>
            <p className="text-white font-black text-sm uppercase tracking-widest">
              {(companyName || 'Koniza Financial Solutions').toUpperCase()}
            </p>
            <p className="text-[#93c5fd] text-[11px] mt-1">
              {terms?.title || 'Terms and Conditions'}{updated ? ` — last updated ${updated}` : ''}
            </p>
          </div>

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className={[
              'p-4 sm:p-6 max-h-[360px] sm:max-h-[430px] overflow-y-auto leading-relaxed text-[13px] sm:text-sm',
              'text-gray-900 dark:text-slate-200 bg-white dark:bg-slate-800',
              '[&_h1]:text-lg [&_h1]:font-bold [&_h2]:text-base [&_h2]:font-bold [&_h3]:font-bold',
              '[&_h4]:font-bold [&_h4]:text-[#1a365d] dark:[&_h4]:text-blue-300 [&_h4]:mt-5 [&_h4]:mb-2',
              '[&_h5]:font-semibold [&_h5]:mt-3 [&_h5]:mb-1',
              '[&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-2 [&_li]:mb-1',
              '[&_a]:text-blue-700 dark:[&_a]:text-blue-300 [&_a]:underline',
            ].join(' ')}
            style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}
          >
            {!terms && <p className="text-gray-500 dark:text-slate-400">Loading the Terms & Conditions…</p>}
            {terms?.html && <div dangerouslySetInnerHTML={{ __html: terms.html }} />}
            {error && (
              <p className="text-rose-700 dark:text-rose-300">
                {error}{' '}
                <a href={terms?.page_url} target="_blank" rel="noopener noreferrer">Read them on our website</a>, then come back to agree.
              </p>
            )}
          </div>
        </div>
        {terms?.page_url && (
          <p className="mt-2 text-center text-[11px] text-gray-500 dark:text-slate-400">
            <a href={terms.page_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-700 dark:hover:text-blue-300">
              Open the Terms & Conditions on our website
            </a>
          </p>
        )}
      </div>
    </div>
  );
};

export default TermsAndConditionsViewer;
